import { usePage } from "@inertiajs/react"
import { Suspense, lazy, useEffect, useState } from "react"

import type { FlashData } from "@/types"

// Sonner (and its lucide icon set) is ~11 KiB that every page used to pull
// into the shared bundle via static imports in the layout. Marketing pages
// never fire a flash, so that was dead weight on the home page's critical JS.
// The whole toast surface (the Toaster and the toast() calls) lives in the
// lazily loaded FlashToasts chunk and ships only once a flash arrives.
const FlashToasts = lazy(() =>
  import("@/components/flash-toasts").then((m) => ({
    default: m.FlashToasts,
  })),
)

function hasFlash(flash: FlashData): boolean {
  return Boolean(flash.alert) || Boolean(flash.notice)
}

// Mounts the toast surface lazily the first time a flash message appears and
// keeps it mounted thereafter, so a flash raised on a later client-side
// navigation still has somewhere to render. Until then, zero sonner bytes
// ship. Firing the toast is FlashToasts' job — a toast() fired out here would
// race the chunk load, run before any Toaster is mounted, and be dropped.
export function FlashToaster() {
  const { flash } = usePage()
  const [armed, setArmed] = useState(() => hasFlash(flash))

  useEffect(() => {
    if (!hasFlash(flash)) return
    const timeout = setTimeout(() => setArmed(true), 0)
    return () => clearTimeout(timeout)
  }, [flash])

  if (!armed) return null

  return (
    <Suspense fallback={null}>
      <FlashToasts />
    </Suspense>
  )
}
